import { AddCircle, RemoveCircle } from '@mui/icons-material';
import { Box, Button, IconButton, Typography } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import Card from 'components/Card/Card';
import CardContent from 'components/CardContent/CardContent';
import Image from 'components/Image/Image';
import { useGame } from 'contexts/Game/Game';
import { useSounds } from 'contexts/Sounds/Sounds';
import { capitalize, getAntUnlock, getEntityTypeGroup } from 'helpers';
import { CSSProperties, useEffect, useState } from 'react';
import * as setup from 'setup';

export default function AntControls({ info, type }: AntControlsProps) {

    const [disableBuy, setDisableBuy] = useState(true);
    const [disableAllocationDecrease, setDisableAllocationDecrease] = useState(true);
    const [disableAllocationIncrease, setDisableAllocationIncrease] = useState(true);
    const [imageStyles, setImageStyles] = useState<CSSProperties>({});
    const [typeGroup, setTypeGroup] = useState<AntTypeGroup | null>();
    const [typeSetup, setTypeSetup] = useState<AntSetup | null>(null);
    const [typeState, setTypeState] = useState<GameStateAnt | null>(null);
    const [typeUnlocked, setTypeUnlocked] = useState(false);
    const game = useGame();
    const sounds = useSounds();

    useEffect(() => {
        // Get type group
        const newTypeGroup = getEntityTypeGroup<AntTypeGroup>(type);
        setTypeGroup(newTypeGroup);

        // Get type setup
        const newTypeSetup = setup.ants[type];
        setTypeSetup(newTypeSetup);

        // Get type state
        const newTypeState = game.entities.ants[newTypeGroup] as GameStateAnt;
        setTypeState(newTypeState);

        // Get type unlocked
        const newTypeUnlocked = game.unlocks[getAntUnlock(type)];
        setTypeUnlocked(newTypeUnlocked);

        // Get image styles
        const newImageStyles = {
            height: Math.round(40 * ((newTypeSetup.size || 1) / Math.max(setup.ants.explorer.size || 1, setup.ants.warrior.size || 1, setup.ants.worker.size || 1))),
        };
        setImageStyles(newImageStyles);
    }, [
        game.entities.ants,
        game.unlocks,
        type,
    ]);

    useEffect(() => {
        if (typeSetup && typeState) {
            const disableAll = !typeUnlocked || game.finished;

            // Check buy button
            const newDisableBuy = disableAll || game.calories < (typeSetup.creationCalories || 0);
            setDisableBuy(newDisableBuy);

            // Check allocation decrease button
            const newDisableAllocationDecrease = disableAll || !game.unlocks.portfolioTool || !typeState.allocation;
            setDisableAllocationDecrease(newDisableAllocationDecrease);

            // Check allocation increase button
            const newDisableAllocationIncrease = disableAll || !game.unlocks.portfolioTool || game.entities.ants.allocation === 1 || typeState.allocation === 1;
            setDisableAllocationIncrease(newDisableAllocationIncrease);
        }
    }, [
        game.calories,
        game.entities.ants.allocation,
        game.finished,
        game.unlocks.portfolioTool,
        typeSetup,
        typeState,
        typeUnlocked,
    ]);

    function handleBuy() {
        game.createEntity('ant', type);
        sounds.play('buy', 0.2);
    }

    function handleAllocation(increase: boolean) {
        game.sounds.play(increase ? 'portfolio-add' : 'portfolio-remove', 0.5);
        game.changeAntAllocation(type, increase);
    }

    return <Card align="center" id={`antControls${capitalize(type)}`} info={typeUnlocked ? info : null}>
        <CardContent locked={!typeUnlocked}>
            <Box alignItems="center" display="flex" height={56} justifyContent="center" padding={1}>
                <Image alt={`Illustration of a ${type} ant`} src={`/assets/ant/${type}/ant-${type}-controls.png`} style={imageStyles} />
            </Box>
            <Typography aria-hidden component="h2" variant="roboto518">
                {typeGroup?.toUpperCase()}
            </Typography>
            <Typography variant="roboto728">
                <Box component="span" sx={visuallyHidden}>
                    total {type} ants:
                </Box>
                {typeState?.active.length}
            </Typography>
            <Box marginTop={2}>
                <Button aria-label={`build 1 ${type} ant`} disabled={disableBuy} onClick={handleBuy}>
                    Build
                </Button>
            </Box>
        </CardContent>
        <CardContent locked={!typeUnlocked || !game.unlocks.colonyHealth}>
            <Typography marginTop={2}>
                <Box component="span" sx={visuallyHidden}>
                    cost of one {type} ant:
                </Box>
                <Box aria-hidden component="span">
                    COST: <b>{typeSetup?.creationCalories} cal</b>
                </Box>
            </Typography>
        </CardContent>
        <CardContent locked={!typeUnlocked || !game.unlocks.portfolioTool}>
            <Box alignItems="center" display="flex" justifyContent="space-between" marginTop={2} marginX={-0.5}>
                <IconButton aria-label={`decrease ${type} ant resource allocation by ${Math.round(setup.antAllocationStep * 100)}%`} disabled={disableAllocationDecrease} onClick={() => handleAllocation(false)}>
                    <RemoveCircle />
                </IconButton>
                <Typography marginX={-1} variant="roboto721">
                    <Box component="span" sx={visuallyHidden}>
                        {type} ant resource allocation:
                    </Box>
                    {Math.round((typeState?.allocation || 0) * 100)}%
                </Typography>
                <IconButton aria-label={`increase ${type} ant resource allocation by ${Math.round(setup.antAllocationStep * 100)}%`} disabled={disableAllocationIncrease} onClick={() => handleAllocation(true)}>
                    <AddCircle />
                </IconButton>
            </Box>
        </CardContent>
    </Card>;

}